/**
 * Flatbox-leads och deras Flatbox-offerter (flatbox_offers.leadId).
 *
 * Ren logik (bara importer med .js-ändelse): används av leadsidan,
 * leadspanelen, Flatbox-konfiguratorn (sub-appen) och scripts/flatbox-link-leads.mjs.
 */
import { matchaFlatboxOffertKund } from './kundmatchning.js';

/** Produkten är Flatbox (samma regel som konfiguratorlänken i Produkter). */
export function isFlatboxProduct(product) {
  return /flatbox/i.test(product?.name || '');
}

/** Leadet gäller Flatbox: productId pekar på en Flatbox-produkt. */
export function isFlatboxLead(lead, products) {
  if (!lead?.productId) return false;
  return isFlatboxProduct((products || []).find(p => p.id === lead.productId));
}

/** Offertdokument (flatbox_offers) grupperade per leadId. Utan leadId hoppas över. */
export function flatboxOfferterPerLead(docs) {
  const map = {};
  for (const d of docs || []) if (d?.leadId) (map[d.leadId] ||= []).push(d);
  return map;
}

export function arSignerad(doc) {
  return !!doc?.offer?.signeringsdatum;
}

/**
 * Etikett på leadkortet: "Signerad" om någon offert är signerad, annars
 * antalet offerter. Inga offerter → null.
 */
export function flatboxEtikett(docs) {
  const n = (docs || []).length;
  if (!n) return null;
  if (docs.some(arSignerad)) return { text: 'Signerad', signerad: true };
  return { text: `${n} ${n === 1 ? 'offert' : 'offerter'}`, signerad: false };
}

export function flatboxUpplagg(config) {
  if (config?.impenza) return 'Impenza';
  return config?.deployment === 'onprem' ? 'On-prem' : 'SaaS';
}

/** Månadspriset ur priceSnapshot: prova-på-priset under kampanj, annars ordinarie. */
export function flatboxManadspris(doc) {
  const ps = doc?.priceSnapshot || {};
  if (doc?.config?.campaignActive && ps.campaignMonthly != null) return ps.campaignMonthly;
  return ps.monthly ?? null;
}

/** En rad i leadets offertlista. */
export function flatboxOffertRad(doc) {
  const off = doc?.offer || {};
  return {
    offerNo: off.offerNo || '',
    upplagg: flatboxUpplagg(doc?.config),
    kampanj: !!doc?.config?.campaignActive,
    anvandare: doc?.priceSnapshot?.tier ? `${doc.priceSnapshot.tier} anv.`
      : doc?.config?.users != null ? `${doc.config.users} anv.` : '',
    manadspris: flatboxManadspris(doc),
    datum: off.offerDate || '',
    signerad: arSignerad(doc),
    signeringsdatum: off.signeringsdatum || '',
    skickad: !!doc?.sent,
  };
}

/** Platshållare ("-", "–", "—", bara blanksteg) räknas som tomt → undefined. */
export function faltVarde(v) {
  const s = String(v ?? '').trim();
  return s && !/^[-–—]+$/.test(s) ? s : undefined;
}

const POSTNR = /^(\d{3})\s?(\d{2})$/;
const POSTORT = /(\d{3})\s?(\d{2})\s+(.+)$/;
const utanSlutkomma = s => s.replace(/[\s,]+$/, '');

/**
 * Delar en fritextadress i adress, postnr ("NNN NN") och ort. Delarna skiljs
 * av radbrytning eller komma. Sista delen "postnr ort", eller postnr och ort som
 * två sista delar, blir egna fält. Annars hamnar allt i adress.
 * "Box 1029, 164 22 Kista"       → { adress: 'Box 1029', postnr: '164 22', ort: 'Kista' }
 * "Dusavägen 1, 35270, VÄXJÖ,"   → { adress: 'Dusavägen 1', postnr: '352 70', ort: 'VÄXJÖ' }
 */
export function delaAdress(addr) {
  const s = utanSlutkomma(String(addr || '').trim());
  const t = s.split(/(\n|,)/); // delar på jämna index, avskiljare på udda
  const sista = t[t.length - 1];
  const m = sista.match(POSTORT);
  if (m) {
    const adress = utanSlutkomma(t.slice(0, -1).join('') + sista.slice(0, m.index)).trim();
    return { adress, postnr: `${m[1]} ${m[2]}`, ort: m[3].trim() };
  }
  const pn = t.length >= 3 && t[t.length - 3].trim().match(POSTNR);
  if (pn && sista.trim() && !/\d/.test(sista)) {
    return { adress: utanSlutkomma(t.slice(0, -4).join('')).trim(), postnr: `${pn[1]} ${pn[2]}`, ort: sista.trim() };
  }
  return { adress: s };
}

/**
 * Indata till Time Flow (src/lib/timeflowExport.js) för en signerad offert.
 * Bara fält som finns i offerten – tomma värden och platshållare utelämnas.
 * Projektnumret i Time Flow är offertnumret. Saknas projektnamn
 * (företagsomfattning) blir det "Flatbox – <kund>".
 * Priser per fakturaperiod: kvartal under prova-på (nya modellen), annars
 * enligt config.billing (helår = 12 mån). Kampanjfälten bara för kampanjoffert.
 * produkt/periodFran/periodTill/anvandare/kampanj blir noteringen på artikelregistreringen.
 * Saknas i datamodellen: kund.momsnr, adress2, betalvillkorDagar samt
 * faktura.datum och faktura.text.
 */
export function toTimeFlowInput(doc) {
  const off = Object.fromEntries(Object.entries(doc?.offer || {}).map(([k, v]) => [k, faltVarde(v)]));
  const c = doc?.config || {};
  const ps = doc?.priceSnapshot || {};
  // Samma kampanjvillkor som flatboxManadspris
  const kampanj = !!c.campaignActive && ps.campaignMonthly != null;
  const manader = (kampanj && c.campaignModel === 2) || c.billing !== 'annual' ? 3 : 12;
  const perPeriod = n => (n == null ? undefined : n * manader);
  const pris = perPeriod(kampanj ? ps.campaignMonthly : ps.monthly);
  // Fakturerad period: första perioden från licensstart, högst t.o.m. licensslut
  const periodTill = off.licenseStart && [kampanjSlutDatum(off.licenseStart, manader), off.licenseEnd]
    .filter(Boolean).sort()[0];
  // Licensierade användare: övre gränsen i prissteget ("1–25" → 25), annars config.users
  const steg = String(ps.tier || '').match(/(\d+)\s*$/);
  const anvandare = steg ? Number(steg[1]) : (c.users != null ? Number(c.users) : undefined);
  return {
    kund: {
      namn: off.custName,
      orgnr: off.custOrg,
      ...(off.custAddr ? delaAdress(off.custAddr) : {}),
      fakturaEpost: off.invoiceEmail,
      kundensReferens: off.invoiceRef,
    },
    projekt: {
      namn: off.projName || (off.custName ? `Flatbox – ${off.custName}` : undefined),
      nummer: off.offerNo,
    },
    faktura: {
      nummer: off.offerNo,
      // Under kampanj är första fakturan en period till kampanjpris (SaaS – ingen uppstartsavgift)
      belopp: kampanj ? pris : ps.firstInvoice,
      prisPerEnhet: pris,
      period: [off.licenseStart, off.licenseEnd].filter(Boolean).join(' – ') || undefined,
      ...(kampanj ? { ordinariePrisPerEnhet: perPeriod(ps.monthly), kampanjSlut: off.licenseEnd } : {}),
      produkt: 'Flatbox',
      periodFran: off.licenseStart,
      periodTill,
      anvandare,
      kampanj,
    },
  };
}

/** Senaste först: offertdatum, därefter offertnummer. */
export function sorteraFlatboxOfferter(docs) {
  return [...(docs || [])].sort((a, b) =>
    String(b.offer?.offerDate || '').localeCompare(String(a.offer?.offerDate || ''))
    || String(b.offer?.offerNo || '').localeCompare(String(a.offer?.offerNo || ''), 'sv', { numeric: true }));
}

/** Leadets huvudkontakt, annars kundens primära, annars första. */
export function valjKontakt(contacts, mainContactId) {
  const list = contacts || [];
  return list.find(c => c.id === mainContactId) || list.find(c => c.primary) || list[0] || null;
}

// ── Kampanj (pilot/prova-på) på leadet ──
// Samma datumregel som konfiguratorn (flatbox/kampanj.js): slut = leveransdatum
// + campaignMonths − 1 dag. Dupliceras här så att modulen saknar importer.
const kampanjSlutDatum = (leverans, manader) => {
  const p = String(leverans || '').split('-');
  const d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]) || 1, 12);
  d.setMonth(d.getMonth() + Number(manader));
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Är offerten en kampanj (pilot eller prova-på)? Nya modellen eller äldre SaaS-projektkampanj. */
export function arKampanjoffert(doc) {
  const c = doc?.config || {};
  return c.deployment === 'saas' && !!c.campaignActive && (c.campaignModel === 2 || c.scope === 'project');
}

/**
 * Leadets kampanj: kampanjofferten med leveransdatum och senast slutdatum.
 * → { offerNo, slut, pilot } | null. pilot = 100 % rabatt.
 */
export function kampanjForLead(docs) {
  const kandidater = (docs || []).filter(arKampanjoffert).filter(d => d.offer?.planeradLeveransdag)
    .map(d => ({
      offerNo: d.offer.offerNo,
      slut: kampanjSlutDatum(d.offer.planeradLeveransdag, d.config.campaignMonths),
      pilot: Number(d.config.campaignDiscount) >= 1,
    }));
  kandidater.sort((a, b) => b.slut.localeCompare(a.slut));
  return kandidater[0] || null;
}

/** "Pilot t.o.m. 2027-05-01" / "Kampanj t.o.m. …" */
export const kampanjText = k => (k ? `${k.pilot ? 'Pilot' : 'Kampanj'} t.o.m. ${k.slut}` : '');

/** Datum för de två påminnelserna: samtal 45 dagar före slutet, och på slutdagen. */
export function pilotPaminnelseDatum(slut) {
  const p = String(slut).split('-');
  const d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]), 12);
  d.setDate(d.getDate() - 45);
  const samtal = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { samtal, slut };
}

/**
 * Leadet en offert ska kopplas till: offertens kund matchas mot CRM-kunderna
 * (org.nr → namn → e-postdomän, första träffen som i analysexporten), och
 * kunden ska ha exakt ett Flatbox-lead. Samma regel för flatbox-link-leads
 * och den automatiska kopplingen i konfiguratorn.
 * @param off           offer{} (custOrg, custName, custEmail)
 * @param kunder        crmCompanies [{ id, name, orgNumber, website }]
 * @param flatboxLeads  Flatbox-leads [{ id, companyId, … }]
 * → { lead, kund, via } | { lead: null, orsak: 'ingen kund'|'inget Flatbox-lead'|'flera Flatbox-leads', kund, leads }
 */
export function leadForOffert(off, kunder, flatboxLeads) {
  const m = matchaFlatboxOffertKund(off, kunder);
  const kund = m.kandidater[0];
  if (!kund) return { lead: null, orsak: 'ingen kund', kund: null, leads: [], kandidater: m.kandidater };
  const leads = (flatboxLeads || []).filter(l => l.companyId === kund.id);
  if (leads.length !== 1) {
    return { lead: null, orsak: leads.length ? 'flera Flatbox-leads' : 'inget Flatbox-lead', kund, leads, kandidater: m.kandidater };
  }
  return { lead: leads[0], kund, via: m.via, kandidater: m.kandidater };
}
